import {$s} from '@bitstillery/common/app'
import {merge_deep, parse_query_params_for_filters, text_before} from '@bitstillery/common/lib/utils'
import type {StateCommon} from '@bitstillery/common/lib/state'

/** When true, the next browser `env()` merges client URL state then immediately applies matchMedia (same turn). */
let defer_viewport_layout_for_ssr_hydration = false

/** Call when hydrating from SSR so the first client `env()` can merge `window.location` before breakpoint setup. */
export function mark_ssr_hydration_env_deferral(): void {
    defer_viewport_layout_for_ssr_hydration = true
}

function update_env(env_config: StateCommon['env'], layout: StateCommon['env']['layout']) {
    const spacer = 8
    env_config.layout = layout

    let query_spacer

    if (layout === 'desktop') {
        query_spacer = spacer * 3
    } else if (layout === 'tablet') {
        query_spacer = spacer * 1.5
    } else if (layout === 'mobile') {
        query_spacer = spacer
    }

    // Only manipulate DOM in browser context
    if (!globalThis.__SSR_MODE__) {
        // Remove old layout classes and add the current one, preserving any other classes.
        const html_element = document.documentElement
        html_element.classList.remove('desktop', 'tablet', 'mobile')
        html_element.classList.add(layout)
        // A viewport between two breakpoints (fractional widths) matches no layout; keep the last spacer.
        if (query_spacer !== undefined) {
            document.documentElement.style.setProperty('--query-spacer', `${query_spacer}px`)
        }
    }
}

export default function env(env_config: StateCommon['env']) {
    if (globalThis.__SSR_MODE__) {
        // Set sensible defaults for SSR (1920x1080 desktop)
        Object.assign(env_config, {
            browserName: '',
            ctrl_down: false,
            isBrowser: false,
            isChrome: false,
            isFirefox: false,
            isSafari: false,
            layout: 'desktop',
            ua: '',
        })
        // Initialize layout-dependent state for SSR
        update_env(env_config, 'desktop')

        // Populate $s.env.uri and $s.env.qs during SSR using isomorphic path handling
        // Use __SSR_URL__ which is set by mithril's SSR system
        if (globalThis.__SSR_URL__) {
            const ssr_url = globalThis.__SSR_URL__
            try {
                // Parse the SSR URL to extract pathname and query string
                const url = new URL(ssr_url)
                // Handle hash-based routing (#!) if present, otherwise use pathname
                const {hash} = url
                if (hash.startsWith('#!')) {
                    // Hash-based routing: extract pathname from hash
                    const hash_path = hash.replace('#!', '')
                    const hash_parts = hash_path.split('?')
                    $s.env.uri = hash_parts[0] || '/'
                    const hash_query = hash_parts[1] || ''
                    const qs = parse_query_params_for_filters(hash_query)
                    // Clear existing qs and merge new values
                    for (const key of Object.keys($s.env.qs)) {
                        delete $s.env.qs[key]
                    }
                    merge_deep($s.env.qs, qs)
                } else {
                    // Pathname-based routing: use pathname directly
                    $s.env.uri = url.pathname || '/'
                    const qs = parse_query_params_for_filters(url.search)
                    // Clear existing qs and merge new values
                    for (const key of Object.keys($s.env.qs)) {
                        delete $s.env.qs[key]
                    }
                    merge_deep($s.env.qs, qs)
                }
            } catch (error) {
                // Fallback: try to parse as relative URL or use defaults
                console.warn('[SSR] Failed to parse __SSR_URL__:', ssr_url, error)
                $s.env.uri = '/'
            }
        } else {
            // Fallback if __SSR_URL__ is not available
            $s.env.uri = '/'
        }

        return
    }

    // Browser-only code below
    const ua = navigator.userAgent.toLowerCase()

    if (defer_viewport_layout_for_ssr_hydration) {
        defer_viewport_layout_for_ssr_hydration = false
        Object.assign(env_config, {
            browserName: '',
            ctrl_down: false,
            isBrowser: Boolean(globalThis.navigator),
            isChrome: Boolean(globalThis.chrome),
            isFirefox: ua.includes('firefox'),
            isSafari: ua.indexOf('safari') >= 0 && ua.indexOf('chrome') < 0,
            ua,
        })
        $s.env.uri = pathname_from_location(window.location)
        for (const key of Object.keys($s.env.qs)) {
            delete $s.env.qs[key]
        }
        merge_deep($s.env.qs, Object.fromEntries(query_string_from_location(window.location)))
        // Apply matchMedia in the same turn so layout matches the viewport before paint (avoids cart/overlay “settling” animations after SSR).
        env(env_config)
        return
    }

    Object.assign(env_config, {
        browserName: '',
        ctrl_down: false,
        isBrowser: Boolean(globalThis.navigator),
        isChrome: Boolean(globalThis.chrome),
        isFirefox: ua.includes('firefox'),
        isSafari: ua.indexOf('safari') >= 0 && ua.indexOf('chrome') < 0,
        layout: 'mobile',
        ua,
    })

    // Matches $breakpoint-20; query from css variable after Discover UI refactor.
    const styles = getComputedStyle(document.documentElement)
    const breakpoint_20 = Number(styles.getPropertyValue('--breakpoint-20').trim().replace('px', ''))
    const breakpoint_40 = Number(styles.getPropertyValue('--breakpoint-40').trim().replace('px', ''))
    const is_desktop = window.matchMedia(`(min-width: ${breakpoint_40}px)`)

    const is_tablet = window.matchMedia(`(min-width: ${breakpoint_20}px) and (max-width: ${breakpoint_40 - 1}px)`)
    const is_mobile = window.matchMedia(`(max-width: ${breakpoint_20 - 1}px)`)

    is_desktop.addEventListener('change', (e) => {
        if (e.matches) {
            update_env(env_config, 'desktop')
        }
    })
    is_tablet.addEventListener('change', (e) => {
        if (e.matches) {
            update_env(env_config, 'tablet')
        }
    })
    is_mobile.addEventListener('change', (e) => {
        if (e.matches) {
            update_env(env_config, 'mobile')
        }
    })

    const layout = is_desktop.matches ? 'desktop' : is_tablet.matches ? 'tablet' : is_mobile.matches ? 'mobile' : 'unknown'
    update_env(env_config, layout)

    window.addEventListener('locationchange', function (e) {
        const target = e.target
        if (!(target instanceof Window)) return
        $s.env.uri = pathname_from_location(target.location)

        for (const key of Object.keys($s.env.qs)) {
            delete $s.env.qs[key]
        }
        const qs = parse_query_params_for_filters(query_string_raw_from_location(target.location))
        merge_deep($s.env.qs, qs)
    })

    $s.env.uri = pathname_from_location(window.location)
    const qs = parse_query_params_for_filters(query_string_raw_from_location(window.location))
    merge_deep($s.env.qs, qs)

    // Track control/meta key state (Ctrl on Windows/Linux, Cmd on Mac)
    window.addEventListener('keydown', (e) => {
        if (e.key === 'Control' || e.key === 'Meta') {
            $s.env.ctrl_key = true
        }
    })

    window.addEventListener('keyup', (e) => {
        if (e.key === 'Control' || e.key === 'Meta') {
            $s.env.ctrl_key = false
        }
    })
}

export function pathname_from_location(location: Location): string {
    return location.hash ? text_before(location.hash.replace('#!', ''), '?') : location.pathname
}

export function query_string_from_location(location: Location): URLSearchParams {
    return new URLSearchParams(location.hash ? location.hash.replace('#!', '').split('?')[1] : location.search)
}

export function query_string_raw_from_location(location: Location): string {
    return location.hash ? (location.hash.replace('#!', '').split('?')[1] ?? '') : location.search
}
